import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../app/theme';
import { lineKinds, lineKm, type MapData, type MapPoint } from '../../lib/map';
import { useMapLook } from '../../lib/mapLook';
import { ModeIcon } from '../icons/ModeIcon';
import { HeightScale, isMeans } from './heightScale';
import type { Follow, MapPlace, MapPoi, Padding, SkyAt } from './TripMap';

/** The map and its library load only on a page that shows one. */
const TripMap = lazy(() => import('./TripMap'));

interface Props {
  data: MapData;
  label: string;
  padding?: Padding | undefined;
  focus?: { lat: number; lon: number } | null | undefined;
  show?: readonly Pick<MapPoint, 'lat' | 'lon'>[] | null | undefined;
  /** Places the view is fitted to each time they change: the way picked. */
  fit?: readonly Pick<MapPoint, 'lat' | 'lon'>[] | null | undefined;
  cooperative?: boolean | undefined;
  keepView?: boolean | undefined;
  /** Its view kept under this key: left and come back to, it is where it was. */
  viewKey?: string | undefined;
  /** The moment its sky is shown at: the night, a plane in the air. */
  sky?: SkyAt | null | undefined;
  onView?: ((center: { lat: number; lon: number }) => void) | undefined;
  onPlace?: ((place: MapPlace) => void) | undefined;
  onSpot?: ((point: { lat: number; lon: number; x: number; y: number }) => void) | undefined;
  onLine?: ((stop: string) => void) | undefined;
  /** Told a station or a sight of the map pressed. */
  onPoi?: ((poi: MapPoi) => void) | undefined;
  onPoint?: ((key: string) => void) | undefined;
  /** The way picked, standing out on the map. */
  way?: string | null | undefined;
  /** How near the view comes when it is fitted: a city's streets, not a trip's countries. */
  closest?: number | undefined;
  /** Ways drawn thin, a town's lines, the one picked not widened. */
  thin?: boolean | undefined;
  /** Told a point the map was held on: what is there. */
  onHold?: ((point: { lat: number; lon: number; x: number; y: number }) => void) | undefined;
  /** The map's own buttons: one's location and its look. */
  controls?: boolean | undefined;
  /** Told a pin dragged to a new point. */
  onPinMove?: ((key: string, point: { lat: number; lon: number }) => void) | undefined;
  /** A point a box is shown over, told where it is as the map moves. */
  follow?: Follow | null | undefined;
}

/** A map of places and ways, with a legend of the ways drawn. */
export function MapPanel({
  data,
  label,
  padding,
  focus,
  show,
  fit,
  cooperative,
  keepView,
  viewKey,
  sky,
  onView,
  onPlace,
  onSpot,
  onLine,
  onPoi,
  way,
  closest,
  onPoint,
  thin,
  onHold,
  controls,
  onPinMove,
  follow,
}: Props) {
  const { t, i18n } = useTranslation();
  const kinds = lineKinds(data);
  const km = lineKm(data);
  const [open, setOpen] = useState<string | null>(null);
  const look = useMapLook();
  const theme = useTheme();
  // Means drawn by their heights: a way of them pressed says its scale.
  const climbing = new Set(
    data.lines.flatMap((line) => (line.heights === undefined ? [] : [line.kind])),
  );
  return (
    <div className="map-panel">
      <Suspense fallback={<div className="trip-map" />}>
        <TripMap
          data={data}
          label={label}
          padding={padding}
          focus={focus}
          show={show}
          fit={fit}
          cooperative={cooperative}
          keepView={keepView}
          viewKey={viewKey}
          sky={sky}
          onView={onView}
          onPlace={onPlace}
          onSpot={onSpot}
          onLine={onLine}
          onPoi={onPoi}
          way={way}
          closest={closest}
          onPoint={onPoint}
          thin={thin}
          onHold={onHold}
          controls={controls}
          onPinMove={onPinMove}
          follow={follow}
        />
      </Suspense>
      {kinds.length > 0 && (
        <ul className="map-legend" aria-label={t('map.legend')}>
          {kinds.map((kind) => (
            <li key={kind}>
              {/* A way by its mark, its name when pointed at; pressed, how far it goes and, drawn
                  by its height, its colours from the ground up. */}
              <button
                type="button"
                className="legend-item"
                title={t(`map.kind.${kind}`)}
                aria-pressed={open === kind}
                aria-expanded={open === kind}
                onClick={() => {
                  setOpen(open === kind ? null : kind);
                }}
              >
                <span className={`swatch swatch-${kind}`} aria-hidden="true" />
                <ModeIcon mode={kind} />
                <span className="visually-hidden">{t(`map.kind.${kind}`)}</span>
              </button>
              {open === kind && (
                <div className="map-legend-pop">
                  <span className="map-legend-km">
                    {t('map.kindKm', {
                      kind: t(`map.kind.${kind}`),
                      km: Math.round(Number(km.get(kind))).toLocaleString(i18n.language),
                    })}
                  </span>
                  {look.heights && isMeans(kind) && climbing.has(kind) && (
                    <HeightScale kind={kind} dark={theme === 'dark'} />
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
